import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Paper,
  Stack,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { sessionService } from '../../../services/apiService';
import { formatDate } from '../../../utils/dateFormat';
import { showToast } from '../../../utils/toast';
import SessionForm from '../../../components/Forms/SessionForm';

const SessionsTab = ({ onAddRef }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await sessionService.getAll();
      const sessionsData = res?.data || res || [];
      setSessions(Array.isArray(sessionsData) ? sessionsData : []);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const handleAdd = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  useEffect(() => {
    if (onAddRef) {
      onAddRef(handleAdd);
    }
  }, [handleAdd, onAddRef]);

  const handleEdit = (row) => {
    setEditing(row);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleModalSave = async (formData) => {
    try {
      setModalLoading(true);
      if (editing) {
        await sessionService.update(editing.id, formData);
        showToast.success('Session updated successfully');
      } else {
        await sessionService.create(formData);
        showToast.success('Session created successfully');
      }
      await loadSessions();
      handleModalClose();
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to save session';
      showToast.error(message);
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1.5, md: 2 }, 
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
    }}>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : sessions.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No sessions found
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: { xs: 1.5, sm: 1.75 },
          }}
        >
          {sessions.map((session) => (
            <Paper
              key={session.id}
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 2.4 },
                borderRadius: 2,
                borderColor: 'divider',
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 2,
                  borderColor: 'primary.main',
                },
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
                      {session.name || '—'}
                    </Typography>
                    <Chip
                      label={session.is_active ? 'Active' : 'Inactive'}
                      color={session.is_active ? 'success' : 'default'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                  
                  {session.academic_year && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Academic Year:</strong> {session.academic_year}
                    </Typography>
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Start Date:</strong> {formatDate(session.start_date)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>End Date:</strong> {formatDate(session.end_date)}
                    </Typography>
                  </Stack>

                  {session.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {session.description}
                    </Typography>
                  )}
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(session)}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.light',
                        color: 'primary.dark',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      <SessionForm
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSave}
        initialData={editing}
        loading={modalLoading}
        title={editing ? 'Edit Session' : 'Add New Session'}
      />
    </Box>
  );
};

export default SessionsTab;
